// src/pages/LeadGenerator.tsx
import React, { useState, useEffect, useContext } from 'react';
import {
  Container, Typography, Grid, Box, Paper, Chip, Alert, CircularProgress,
  FormControl, Select, MenuItem, TextField, Button, Checkbox, FormControlLabel,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer, IconButton,
  Collapse, InputLabel
} from '@mui/material';
import { AuthContext } from '../contexts/AuthContext';
import api from '../api/api';
import { useNavigate } from 'react-router-dom';
import { getUserFriendlyError } from '../utils/errorHandler';
import AccountPickerSingle from '../components/AccountPickerSingle';
import { useFieldValidation } from '../hooks/useFieldValidation';
import type { FieldLimit, PairRule } from '../hooks/useFieldValidation';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
// ==== Границы полей: 1-в-1 из StartLeadGeneratorCommand и вложенных конфигов ====
// У числовых полей в модели нет ge/le (только дефолты) — ставим разумные минимумы.
// Исключение: рабочие часы 0..24 (по описанию WorkingHoursConfig).
const LG_LIMITS: Record<string, FieldLimit> = {
  // min/max_response_time: float, дефолты 30/300, без ge/le
  minResponseTime: { min: 0 },
  maxResponseTime: { min: 0 },
  // WorkingHoursConfig.start/stop (часы 0..24)
  workingHoursStart: { min: 0, max: 24, step: 0.5 },
  workingHoursEnd: { min: 0, max: 24, step: 0.5 },
  // SafetyConfig
  safetyMaxPerChat: { min: 1, integer: true },
  safetyMinInterval: { min: 0, integer: true, helper: 'сек' },
  safetyMaxTotalDay: { min: 1, integer: true },
  // AutoResponderConfig.max_messages
  autoResponderMax: { min: 1, integer: true },
};

const LG_PAIRS: PairRule[] = [
  { minKey: 'minResponseTime', maxKey: 'maxResponseTime' },
  { minKey: 'workingHoursStart', maxKey: 'workingHoursEnd', message: 'Конец должен быть позже начала' },
];



interface Account {
  id: string; name: string; phone_number: string;
}

interface ActiveCampaign {
  campaign_id: string;
  account_name: string;
  status: string;
  monitoring_chats_count: number;
  total_triggers_fired: number;
  total_messages_sent: number;
  total_replies_received: number;
  uptime_seconds?: number;
  last_activity_at?: string;
}

const LeadGenerator: React.FC = () => {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [campaigns, setCampaigns] = useState<ActiveCampaign[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [tab, setTab] = useState<'setup' | 'active'>('setup');
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [form, setForm] = useState({
    accountName: '',
    chatIds: '',
    keywords: '',
    stopWords: '',
    geoFilter: '',
    sentimentFilter: 'any' as 'positive' | 'negative' | 'neutral' | 'positive_neutral' | 'any',
    responseStrategy: 'current_chat' as 'current_chat' | 'user_chat' | 'current_if_not_user',
    useAi: true,
    aiPrompt: '',
    templates: [''],
    minResponseTime: '30',
    maxResponseTime: '300',
    typingSimulation: true,
    workingHoursStart: '9.0',
    workingHoursEnd: '21.0',
    processAccumulated: true,
    safetyMaxPerChat: '5',
    safetyMinInterval: '1800',
    safetyMaxTotalDay: '50',
    dedupUser: true,
    dedupDays: '7',
    autoResponderEnabled: false,
    autoResponderMax: '3',
    autoResponderPrompt: '',
    autoResponderTexts: '',
    duplicateLeadsChat: '',
  });

  const update = (key: keyof typeof form, value: any) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const validation = useFieldValidation({ limits: LG_LIMITS, pairs: LG_PAIRS });

  useEffect(() => {
    if (!auth?.isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchData();
    const interval = setInterval(fetchActive, 8000);
    return () => clearInterval(interval);
  }, [auth?.isAuthenticated, navigate]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [accRes, campRes] = await Promise.all([
        api.get('/accounts'),
        api.get('/lead_generator/list')
      ]);
      setAccounts(accRes.data.accounts || []);
      setCampaigns(campRes.data.campaigns || []);
    } catch (err: any) {
      const text = getUserFriendlyError(err, [], 'загрузку');
      setMessage({ text, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchActive = async () => {
    try {
      const res = await api.get('/lead_generator/list');
      setCampaigns(res.data.campaigns || []);
    } catch (err) {
      console.error(err);
    }
  };

  const startCampaign = async () => {
    if (!form.accountName || !form.chatIds.trim()) {
      setMessage({ text: 'Выберите аккаунт и укажите чаты', type: 'error' });
      return;
    }

    if (!validation.validateAll(form)) {
      setMessage({ text: 'Проверьте выделенные поля — есть ошибки валидации', type: 'error' });
      return;
    }

    const payload: any = {
      account_name: form.accountName,
      chat_ids: form.chatIds.split(',').map(s => s.trim()).filter(Boolean),
      sentiment_filter: form.sentimentFilter,
      response_strategy: form.responseStrategy,
      use_ai: form.useAi,
      min_response_time: parseFloat(form.minResponseTime) || 30,
      max_response_time: parseFloat(form.maxResponseTime) || 300,
      typing_simulation: form.typingSimulation,
    };

    if (form.keywords.trim()) payload.keywords = form.keywords.split(',').map(s => s.trim());
    if (form.stopWords.trim()) payload.stop_words = form.stopWords.split(',').map(s => s.trim());
    if (form.geoFilter.trim()) payload.geo_filter = form.geoFilter.split(',').map(s => s.trim());
    if (form.aiPrompt.trim()) payload.ai_prompt = form.aiPrompt;
    if (!form.useAi && form.templates.some(t => t.trim())) {
      payload.templates = form.templates.filter(t => t.trim());
    }
    if (form.duplicateLeadsChat.trim()) payload.duplicate_leads_chat_id = form.duplicateLeadsChat.trim();

    if (form.workingHoursStart && form.workingHoursEnd) {
      payload.working_hours = {
        start: parseFloat(form.workingHoursStart),
        stop: parseFloat(form.workingHoursEnd),
        timezone: 'Europe/Moscow',
        process_accumulated: form.processAccumulated,
      };
    }

    payload.safety = {
      max_per_chat_day: parseInt(form.safetyMaxPerChat) || 5,
      min_interval_sec: parseInt(form.safetyMinInterval) || 1800,
      max_total_day: parseInt(form.safetyMaxTotalDay) || 50,
      dedup_user: form.dedupUser,
      dedup_window_days: parseInt(form.dedupDays) || 7,
    };

    if (form.autoResponderEnabled) {
      payload.auto_responder = {
        enabled: true,
        max_messages: parseInt(form.autoResponderMax) || 3,
        ai_prompt: form.autoResponderPrompt.trim() || null,
        texts: form.autoResponderTexts.trim()
            ? form.autoResponderTexts.split('\n').map(s => s.trim()).filter(Boolean)
            : null,
      };
    }

    setIsLoading(true);
    try {
      await api.post('/lead_generator/start', payload, {
        headers: { 'Content-Type': 'application/json' },
      });

      setMessage({ text: 'Лидогенератор запущен!', type: 'success' });
      setTab('active');
      fetchActive();
    } catch (err: any) {
      const text = getUserFriendlyError(err, [], 'лидогенератор');
      setMessage({ text, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const stopCampaign = async (id: string) => {
    setIsLoading(true);
    try {
      await api.delete(`/lead_generator/stop/${id}`);
      setMessage({ text: 'Остановлено', type: 'info' });
      fetchActive();
    } catch (err: any) {
      const text = getUserFriendlyError(err, [], 'остановку');
      setMessage({ text, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };
  const restartCampaign = async (id: string) => {
  setIsLoading(true);
  try {
    await api.get(`/lead_generator/restart/${id}`);
    setMessage({ text: 'Лидогенератор перезапущен!', type: 'success' });
    fetchActive();
  } catch (err: any) {
    const text = getUserFriendlyError(err, [], 'перезапуск');
    setMessage({ text, type: 'error' });
  } finally {
    setIsLoading(false);
  }
};
  const deleteCampaign = async (id: string) => {
    if (!window.confirm('Удалить кампанию НАВСЕГДА?')) return;
    try {
      await api.get(`/lead_generator/delete/${id}`);
      setMessage({ text: 'Кампания удалена', type: 'info' });
      fetchActive();
    } catch (err: any) {
      const text = getUserFriendlyError(err, [], 'удаление');
      setMessage({ text, type: 'error' });
    }
  };

  const getTabStyle = (current: 'setup' | 'active') => ({
    flex: 1,
    py: 2,
    borderRadius: 0,
    bgcolor: tab === current ? '#E0F2FE' : 'transparent',
    color: tab === current ? '#1E40AF' : '#6B7280',
    fontWeight: tab === current ? 600 : 400,
    '&:hover': { bgcolor: tab === current ? '#DBEAFE' : '#F3F4F6' }
  });

  if (isLoading && accounts.length === 0) {
    return (
      <Container sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography>Загрузка...</Typography>
      </Container>
    );
  }

  return (
    <Container sx={{ py: 6, maxWidth: 'lg' }}>
      {/* === ХЕДЕР — как в нейрокопирайтинге === */}
      <Paper elevation={3} sx={{ p: 4, mb: 4, bgcolor: '#F8FAFC', borderRadius: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid size={{ xs: 12, md: 2 }} sx={{ textAlign: { xs: 'center', md: 'left' } }}>
            <SmartToyIcon sx={{ fontSize: 40, color: '#1E40AF' }} />
          </Grid>
          <Grid size={{ xs: 12, md: 10 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1E3A8A', mb: 1 }}>
              Лидогенератор
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Chip label="ИИ-автоответчик" size="small" sx={{ bgcolor: '#DBEAFE', color: '#1E40AF' }} />
              <Chip label="Реакции на ключевые слова" size="small" sx={{ bgcolor: '#DBEAFE', color: '#1E40AF' }} />
              <Chip label="Гео-фильтр" size="small" sx={{ bgcolor: '#DBEAFE', color: '#1E40AF' }} />
              <Chip label="Рабочие часы" size="small" sx={{ bgcolor: '#DBEAFE', color: '#1E40AF' }} />
              <Chip label="Дублирование лидов в чат" size="small"  sx={{ bgcolor: '#DBEAFE', color: '#1E40AF' }}  />
            </Box>
            <Typography sx={{ color: '#4B5563', fontSize: '0.95rem', lineHeight: 1.6 }}>
              Автоматически реагирует на сообщения в чатах по ключевым словам, гео, тональности, пишет в лс или в чат, дублируя лид в специальный диалог
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* === ОПИСАНИЕ — как в нейрокопирайтинге === */}
      <Box sx={{bgcolor: '#F9FAFB', p: 4, borderRadius: 2, mb: 6}}>
        <Typography variant="h6" sx={{color: '#1E3A8A', mb: 3, fontWeight: 600}}>
          Как работает лидогенератор?
        </Typography>
        <Typography sx={{color: '#4B5563', mb: 3, lineHeight: 1.7}}>
          Аккаунт мониторит указанные чаты. При появлении сообщения с ключевым словом —  отвечает (в чат или в ЛС)
          по шаблону или через AI. Предполагаемый лид дублируется в отдельный чат для личного контроля над процессом.
        </Typography>

        <Box sx={{display: 'flex', flexDirection: {xs: 'column', md: 'row'}, gap: 4}}>
          <Box sx={{flex: 1}}>
            <Typography sx={{color: '#1E40AF', fontWeight: 500, mb: 1}}>Триггеры</Typography>
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5}}>
              <Typography sx={{color: '#4B5563', fontSize: '0.9rem'}}>• Ключевые слова</Typography>
              <Typography sx={{color: '#4B5563', fontSize: '0.9rem'}}>• Гео - объекты</Typography>
              <Typography sx={{color: '#4B5563', fontSize: '0.9rem'}}>• Тональность сообщения</Typography>
              <Typography sx={{color: '#4B5563', fontSize: '0.9rem'}}>• Стоп-слова</Typography>
            </Box>
          </Box>

          <Box sx={{flex: 1}}>
            <Typography sx={{color: '#1E40AF', fontWeight: 500, mb: 1}}>Ответы</Typography>
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5}}>
              <Typography sx={{color: '#4B5563', fontSize: '0.9rem'}}>• В чат</Typography>
              <Typography sx={{color: '#4B5563', fontSize: '0.9rem'}}>• В ЛС</Typography>
              <Typography sx={{color: '#4B5563', fontSize: '0.9rem'}}>• AI или шаблоны</Typography>
              <Typography sx={{color: '#4B5563', fontSize: '0.9rem'}}>• Симуляция набора текста</Typography>
            </Box>
          </Box>

        </Box>

        <Typography sx={{color: '#1E40AF', mt: 4, fontWeight: 600, fontStyle: 'italic'}}>
          Идеально для: продажи услуг, консультаций, записи, лидогенерации с рекламы и органики
        </Typography>
            <Typography sx={{color: '#1E40AF', mt: 4, fontWeight: 600, fontStyle: 'italic'}}>
          Ознакомьтесь с документацией по автоматизации{' '}
          <Typography
              component="a"
              href="https://typhon.gitbook.io/docs/automatizations_telegram/leadgen"
              sx={{textDecoration: 'underline', cursor: 'pointer'}}
          >
            здесь
          </Typography>
          </Typography>

      </Box>

      {message && <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>{message.text}</Alert>}

      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex' }}>
          <Button onClick={() => setTab('setup')} sx={getTabStyle('setup')} startIcon={<PlayArrowIcon />}>
            Настройка
          </Button>
          <Button onClick={() => setTab('active')} sx={getTabStyle('active')} startIcon={<StopIcon />}>
            Мои лидогенераторы ({campaigns.length})
          </Button>
        </Box>

        <Box sx={{ p: 4 }}>
          {tab === 'setup' && (
            <Grid container spacing={3}>
              {/* Твоя форма — ПОЛНОСТЬЮ сохранена */}
              <Grid size={12}>
                <AccountPickerSingle
                  accounts={accounts}
                  selected={form.accountName}
                  onChange={(name) => update('accountName', name)}
                  placeholder="Выберите аккаунт"
                />
              </Grid>

              <Grid size={12}>
                <TextField
                  label="Чаты для мониторинга"
                  value={form.chatIds}
                  onChange={e => update('chatIds', e.target.value)}
                  fullWidth
                  placeholder="@chat, https://t.me/chat, -1001234567890"
                  helperText="Через запятую или по одному в строке"
                />
              </Grid>

              <Grid size={12}>
                <TextField
                  label="Ключевые слова (OR)"
                  value={form.keywords}
                  onChange={e => update('keywords', e.target.value)}
                  fullWidth
                  placeholder="куплю, цена, сколько, интересно"
                  helperText="Бот отреагирует при наличии ЛЮБОГО из слов"
                />
              </Grid>

              <Grid size={12}>
                <TextField
                  label="Стоп-слова"
                  value={form.stopWords}
                  onChange={e => update('stopWords', e.target.value)}
                  fullWidth
                  placeholder="спам, реклама, не интересно"
                  helperText="Приоритет выше ключевых слов"
                />
              </Grid>

              <Grid size={6}>
                <TextField
                  label="Гео-фильтр (город/район)"
                  value={form.geoFilter}
                  onChange={e => update('geoFilter', e.target.value)}
                  fullWidth
                  placeholder="Москва, Питер, Казань"
                />
              </Grid>
              <Grid size={6}>
                <FormControl fullWidth>
                  <InputLabel>Тональность</InputLabel>
                  <Select
                    value={form.sentimentFilter}
                    onChange={e => update('sentimentFilter', e.target.value)}
                    label="Тональность"
                  >
                    <MenuItem value="any">Любая</MenuItem>
                    <MenuItem value="positive">Положительная</MenuItem>
                    <MenuItem value="positive_neutral">Положительная + нейтральная</MenuItem>
                    <MenuItem value="negative">Отрицательная (только с стоп-словами)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={12}>
                <FormControlLabel
                  control={<Checkbox checked={form.useAi} onChange={e => update('useAi', e.target.checked)} />}
                  label="Использовать AI для ответов"
                />
              </Grid>

              {form.useAi ? (
                <Grid size={12}>
                  <TextField
                    label="AI промпт (опционально)"
                    value={form.aiPrompt}
                    onChange={e => update('aiPrompt', e.target.value)}
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="Ты — вежливый менеджер по продажам. Предложи помощь, уточни детали, запиши на консультацию..."
                  />
                </Grid>
              ) : (
                <Grid size={12}>
                  <TextField
                    label="Шаблоны ответов (по одному в строке)"
                    value={form.templates.join('\n')}
                    onChange={e => update('templates', e.target.value.split('\n'))}
                    fullWidth
                    multiline
                    rows={6}
                    placeholder="Привет! Интересует цена?&#10;Можем подобрать под ваш бюджет&#10;..."
                  />
                </Grid>
              )}

              <Grid size={12}>
                <FormControl fullWidth>
                  <InputLabel>Куда отвечать</InputLabel>
                  <Select
                    value={form.responseStrategy}
                    onChange={e => update('responseStrategy', e.target.value)}
                    label="Куда отвечать"
                  >
                    <MenuItem value="current_chat">В текущий чат</MenuItem>
                    <MenuItem value="user_chat">В ЛС пользователю</MenuItem>
                    <MenuItem value="current_if_not_user">Сначала в ЛС, если нельзя — в чат</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={12}>
                <FormControlLabel
                  control={<Checkbox checked={form.workingHoursStart !== ''} onChange={e => {
                    if (!e.target.checked) {
                      update('workingHoursStart', '');
                      update('workingHoursEnd', '');
                    } else {
                      update('workingHoursStart', '9.0');
                      update('workingHoursEnd', '21.0');
                    }
                  }} />}
                  label="Ограничить рабочие часы"
                />
              </Grid>
              {form.workingHoursStart !== '' && (
                <>
                  <Grid size={6}>
                    <TextField label="Начало (час)" type="number" {...validation.fieldProps('workingHoursStart', form, update)} fullWidth />
                  </Grid>
                  <Grid size={6}>
                    <TextField label="Конец (час)" type="number" {...validation.fieldProps('workingHoursEnd', form, update)} fullWidth />
                  </Grid>
                  <Grid size={12}>
                    <FormControlLabel
                      control={<Checkbox checked={form.processAccumulated} onChange={e => update('processAccumulated', e.target.checked)} />}
                      label="Обрабатывать накопленные сообщения вне часов"
                    />
                  </Grid>
                </>
              )}

              <Grid size={12}>
                <FormControlLabel
                  control={<Checkbox checked={form.autoResponderEnabled} onChange={e => update('autoResponderEnabled', e.target.checked)} />}
                  label="Автоответчик в ЛС после первого контакта"
                />
              </Grid>
              {form.autoResponderEnabled && (
                  <>
                    <Grid size={6}>
                      <TextField
                          label="Макс сообщений в ЛС"
                          type="number"
                          {...validation.fieldProps('autoResponderMax', form, update)}
                          fullWidth
                      />
                    </Grid>
                    <Grid size={6}>
                      <TextField
                          label="AI промпт для автоответчика"
                          value={form.autoResponderPrompt}
                          onChange={e => update('autoResponderPrompt', e.target.value)}
                          fullWidth
                          multiline
                          rows={3}
                          placeholder="Ты менеджер. Отправь контакт +79991234567..."
                          helperText="Если заданы тексты ниже — промпт игнорируется"
                      />
                    </Grid>
                    <Grid size={12}>
                      <TextField
                          label="Фиксированные тексты ответов (по одному в строке)"
                          value={form.autoResponderTexts}
                          onChange={e => update('autoResponderTexts', e.target.value)}
                          fullWidth
                          multiline
                          rows={4}
                          placeholder={"Привет! Наш менеджер: +79991234567\nСвяжемся с вами в ближайшее время!"}
                          helperText="Если заполнено — AI не используется, выбирается случайный текст из списка"
                      />
                    </Grid>
                  </>
              )}

              <Grid size={12}>
                <TextField
                  label="Чат для дублирования лидов"
                  value={form.duplicateLeadsChat}
                  onChange={e => update('duplicateLeadsChat', e.target.value)}
                  fullWidth
                  placeholder="@myleads или -1001234567890"
                  helperText="Все сообщения-триггеры будут пересылаться в этот чат"
                />
              </Grid>

              <Grid size={12}>
                <Button fullWidth variant="outlined" onClick={() => setShowAdvanced(!showAdvanced)} endIcon={showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}>
                  Расширенные настройки
                </Button>
              </Grid>

              <Grid size={12}>
                <Collapse in={showAdvanced}>
                  <Box sx={{ p: 3, bgcolor: '#F8FAFC', borderRadius: 2 }}>
                    <Grid container spacing={3}>
                      <Grid size={6}><TextField label="Мин. время до ответа (сек)" type="number" {...validation.fieldProps('minResponseTime', form, update)} fullWidth /></Grid>
                      <Grid size={6}><TextField label="Макс. время до ответа (сек)" type="number" {...validation.fieldProps('maxResponseTime', form, update)} fullWidth /></Grid>
                      <Grid size={12}><FormControlLabel control={<Checkbox checked={form.typingSimulation} onChange={e => update('typingSimulation', e.target.checked)} />} label="Симулировать набор текста" /></Grid>
                      <Grid size={6}><TextField label="Макс ответов в чат/день" type="number" {...validation.fieldProps('safetyMaxPerChat', form, update)} fullWidth /></Grid>
                      <Grid size={6}><TextField label="Мин. интервал между ответами" type="number" {...validation.fieldProps('safetyMinInterval', form, update)} fullWidth /></Grid>
                      <Grid size={6}><TextField label="Макс всего сообщений/день" type="number" {...validation.fieldProps('safetyMaxTotalDay', form, update)} fullWidth /></Grid>
                      <Grid size={12}><FormControlLabel control={<Checkbox checked={form.dedupUser} onChange={e => update('dedupUser', e.target.checked)} />} label="Не писать одному пользователю дважды" /></Grid>
                    </Grid>
                  </Box>
                </Collapse>
              </Grid>

              <Grid size={12}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={startCampaign}
                  disabled={isLoading || !form.accountName || !form.chatIds.trim() || validation.hasErrors}
                  startIcon={isLoading ? <CircularProgress size={24} /> : <PlayArrowIcon />}
                  fullWidth
                   sx={{ py: 2, fontSize: '1.1rem', bgcolor: '#1E3A8A', '&:hover': { bgcolor: '#1E40AF' } }}
                >
                  {isLoading ? 'Запуск...' : 'Запустить'}
                </Button>
              </Grid>
            </Grid>
          )}

          {tab === 'active' && (
  campaigns.length === 0 ? (
    <Typography textAlign="center" py={10} color="text.secondary">
      Нет активных лидогенераторов
    </Typography>
  ) : (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell><strong>ID</strong></TableCell>
            <TableCell><strong>Аккаунт</strong></TableCell>
            <TableCell><strong>Чатов</strong></TableCell>
            <TableCell><strong>Триггеров</strong></TableCell>
            <TableCell><strong>Отправлено</strong></TableCell>
            <TableCell><strong>Статус</strong></TableCell>
            <TableCell align="right"><strong>Действия</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {campaigns.map(c => {
            const isRunning = c.status === 'running';
            const isStopped = c.status === 'stopped';
            const isCompleted = c.status === 'completed';
            const isError = c.status === 'error';

            const statusLabel = isRunning ? 'Работает' :
                                isStopped ? 'Остановлен' :
                                isCompleted ? 'Завершён' :
                                isError ? 'Ошибка' : c.status;

            const statusColor = isRunning ? 'success' :
                                isCompleted ? 'success' :
                                     isStopped ? 'warning' :
                                isError ? 'error' : 'default';



            return (
              <TableRow key={c.campaign_id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/lead_generator/detail/${c.campaign_id}`)}>
                <TableCell sx={{ fontFamily: 'monospace' }}>{c.campaign_id.slice(0, 8)}</TableCell>
                <TableCell>{c.account_name}</TableCell>
                <TableCell>{c.monitoring_chats_count}</TableCell>
                <TableCell sx={{ color: '#10B981' }}>{c.total_triggers_fired}</TableCell>
                <TableCell>{c.total_messages_sent}</TableCell>
                <TableCell>
                  <Chip
                    label={statusLabel}
                    size="small"
                    color={statusColor}
                    sx={{ minWidth: 90 }}
                  />
                </TableCell>
                <TableCell align="right" onClick={e => e.stopPropagation()}>
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                    {isRunning && (
                      <IconButton size="small" color="error" onClick={() => stopCampaign(c.campaign_id)}>
                        <StopIcon />
                      </IconButton>
                    )}
                    {isStopped && (
                      <IconButton size="small" color="success" onClick={() => restartCampaign(c.campaign_id)}>
                        <PlayArrowIcon />
                      </IconButton>
                    )}
                    <IconButton size="small" color="error" onClick={() => deleteCampaign(c.campaign_id)}>
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
)}
        </Box>
      </Paper>
    </Container>
  );
};

export default LeadGenerator;